/**
 * Testimonials Block Component
 *
 * A responsive, token-driven testimonials section for landing pages.
 * Features mobile-first design, dark mode support, and CTA dominance.
 *
 * Design Principles:
 * - Mobile-first: Stack layout on mobile, grid on larger screens
 * - Token-driven: All colors from semantic tokens
 * - Dark-mode intentional: Explicit dark mode styling
 * - CTA dominance: Primary CTA is visually prominent
 *
 * @example
 * ```tsx
 * <Testimonials
 *   testimonials={testimonialData}
 *   title="What Our Customers Say"
 *   primaryCta={{
 *     label: "Start Creating",
 *     ariaLabel: "Start creating your custom design",
 *     onClick: handleCtaClick,
 *   }}
 * />
 * ```
 *
 * @see testimonials.types.ts for type definitions
 * @see docs/Frontend-Upgrade-2025-12/03-landing-blocks.md for documentation
 */

import * as React from 'react';
import {cn} from '@/shared/utils/utils';
import {Button} from '@/shared/components/ui/Button';
import {Heading, Text} from '@/ui/typography';
import {Cluster, Container, Section, Stack} from '@/ui/layout';
import type {TestimonialCardProps, TestimonialsProps} from './testimonials.types';
import {testimonialCardVariants, testimonialsVariants} from './Testimonials.variants';

// =============================================================================
// STAR RATING COMPONENT
// =============================================================================

interface StarRatingProps {
  rating: 1 | 2 | 3 | 4 | 5;
  className?: string;
}

function StarRating({ rating, className }: StarRatingProps): React.ReactElement {
  return (
    <div
      className={cn("flex items-center gap-1", className)}
      role="img"
      aria-label={`${rating} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={cn(
            "h-4 w-4 sm:h-5 sm:w-5 transition-colors",
            star <= rating
              ? "fill-amber-400 text-amber-400 dark:fill-amber-300 dark:text-amber-300"
              : "fill-muted/20 text-muted-foreground/20"
          )}
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

// =============================================================================
// AVATAR COMPONENT
// =============================================================================

interface AvatarProps {
  name: string;
  avatarUrl?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

function Avatar({
  name,
  avatarUrl,
  size = "md",
  className,
}: AvatarProps): React.ReactElement {
  const sizeClasses = {
    sm: "h-8 w-8 text-xs",
    md: "h-10 w-10 sm:h-12 sm:w-12 text-sm",
    lg: "h-14 w-14 sm:h-16 sm:w-16 text-base",
  };

  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={`${name}'s avatar`}
        className={cn(
          "rounded-full object-cover ring-2 ring-border",
          "dark:ring-border",
          sizeClasses[size],
          className
        )}
      />
    );
  }

  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-full",
        "bg-gradient-to-br from-primary/30 to-accent/30",
        "text-foreground font-semibold",
        "dark:from-primary/40 dark:to-accent/40",
        "ring-2 ring-border/50 dark:ring-border/30",
        "shadow-sm",
        sizeClasses[size],
        className
      )}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

// =============================================================================
// TESTIMONIAL CARD COMPONENT
// =============================================================================

/**
 * Individual testimonial card displaying customer review.
 * Modern card design with proper spacing and typography.
 */
function TestimonialCard({
  testimonial,
  variant = "elevated",
  className,
  "data-testid": testId,
}: TestimonialCardProps): React.ReactElement {
  const { name, role, company, quote, avatarUrl, rating, featured } = testimonial;

  return (
    <article
      className={cn(
        testimonialCardVariants({ variant, featured }),
        "flex flex-col justify-between",
        className
      )}
      data-testid={testId}
      data-block="testimonial-card"
    >
      {/* Quote Section */}
      <blockquote className="flex-1 flex flex-col gap-4">
        {rating && (
          <StarRating rating={rating} className="mb-1" />
        )}
        <Text
          size="base"
          variant="default"
          className={cn(
            "leading-relaxed",
            // Modern quote styling - no italics, better readability
            "text-foreground/90 dark:text-foreground/90",
            featured ? "text-lg sm:text-xl" : "text-base sm:text-lg"
          )}
        >
          &ldquo;{quote}&rdquo;
        </Text>
      </blockquote>

      {/* Author info - modern styling */}
      <footer className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-border/20 dark:border-border/10 flex items-center gap-4">
        <Avatar
          name={name}
          {...(avatarUrl ? { avatarUrl } : {})}
          size={featured ? "lg" : "md"}
        />
        <div className="flex flex-col gap-0.5">
          <Text
            size="sm"
            weight="semibold"
            variant="default"
            className="text-foreground dark:text-foreground"
          >
            {name}
          </Text>
          {(role || company) && (
            <Text
              size="xs"
              variant="muted"
              className="text-muted-foreground/80 dark:text-muted-foreground/70"
            >
              {role}
              {role && company && " · "}
              {company}
            </Text>
          )}
        </div>
      </footer>
    </article>
  );
}


// =============================================================================
// GRID LAYOUT HELPER
// =============================================================================

/**
 * Returns responsive grid classes based on item count and layout mode.
 *
 * Mobile-first layout with proper breakpoints:
 * - Mobile (< 768px): 1 column ALWAYS - cards stack vertically
 * - md (768px+): 2 columns for tablet experience
 * - lg (1024px+): 3 columns max (optimal for readability)
 *
 * IMPORTANT: Never use sm:grid-cols-2 - many phones are 640px+ but still need single column!
 */
function getGridClasses(
  count: number,
  layout: TestimonialsProps["layout"]
): string {
  if (layout === "stack") {
    return "flex flex-col gap-6 md:gap-8";
  }

  // Featured layout - same responsive behavior, featured card styled differently
  if (layout === "featured") {
    return [
      "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
      "gap-6 md:gap-8 lg:gap-10",
      "auto-rows-fr",
    ].join(" ");
  }

  // Default grid layout - ALWAYS mobile-first with 1 column base
  // Max 3 columns to ensure cards remain readable
  if (count <= 2) {
    return "grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8";
  }

  // 3+ items: 1 col mobile → 2 col tablet → 3 col desktop (max)
  return [
    "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    "gap-6 md:gap-8 lg:gap-10",
    "auto-rows-fr",
  ].join(" ");
}

// =============================================================================
// TESTIMONIALS COMPONENT
// =============================================================================

/**
 * Testimonials landing page block.
 *
 * Features:
 * - Mobile-first responsive layout
 * - Token-driven design with dark mode
 * - CTA dominance pattern
 * - Accessible star ratings and testimonial cards
 */
function Testimonials({
  testimonials,
  title = "What Our Customers Say",
  subtitle,
  primaryCta,
  secondaryCta,
  maxItems,
  background = "muted",
  layout = "grid",
  spacing = "md",
  className,
  "aria-label": ariaLabel,
  "data-testid": testId,
}: TestimonialsProps): React.ReactElement {
  // Limit testimonials if maxItems is specified
  const displayTestimonials = maxItems
    ? testimonials.slice(0, maxItems)
    : testimonials;

  // Sort to show featured testimonials first
  const sortedTestimonials = [...displayTestimonials].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return 0;
  });

  return (
    <Section
      className={cn(
        testimonialsVariants({ background, layout, spacing }),
        className
      )}
      aria-label={ariaLabel ?? title}
      data-testid={testId}
      data-block="testimonials"
    >

      {/* Gradient orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-1/4 left-1/4 w-full max-w-container-md aspect-square opacity-20 dark:opacity-10 blur-3xl"
          style={{
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.5) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute -bottom-1/4 right-1/4 w-full max-w-container-sm aspect-square opacity-15 dark:opacity-10 blur-3xl"
          style={{
            background: 'radial-gradient(circle, hsl(var(--accent) / 0.4) 0%, transparent 70%)',
          }}
        />
      </div>

      <Container size="7xl" className="relative z-10">
        <Stack gap="lg">
          {/* Header - Modern styling */}
          <Stack gap="md" className="text-center max-w-3xl mx-auto">
            <Heading
              level={2}
              size="display-3"
              weight="medium"
              align="center"
              balance
              className="text-foreground dark:text-foreground"
            >
              {title}
            </Heading>
            {subtitle && (
              <Text
                size="lg"
                variant="muted"
                align="center"
                className="text-muted-foreground/80 dark:text-muted-foreground/70 leading-relaxed"
              >
                {subtitle}
              </Text>
            )}
          </Stack>

          {/* Testimonials Grid */}
          <div className={cn(getGridClasses(sortedTestimonials.length, layout), "overflow-visible")}>
            {sortedTestimonials.map((testimonial) => (
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                {...(testimonial.featured ? { featured: true } : {})}
                {...(testId ? { "data-testid": `${testId}-card-${testimonial.id}` } : {})}
              />
            ))}
          </div>

          {/* CTA Section - Dominance Pattern */}
          {(primaryCta || secondaryCta) && (
            <Cluster
              gap="md"
              justify="center"
              className="pt-4 sm:pt-8"
            >
              {primaryCta && (
                <Button
                  variant="primary"
                  size="lg"
                  {...(primaryCta.onClick ? { onClick: primaryCta.onClick } : {})}
                  aria-label={primaryCta.ariaLabel}
                  data-testid={testId ? `${testId}-cta-primary` : "testimonials-cta-primary"}
                  asChild={!!primaryCta.href}
                >
                  {primaryCta.href ? (
                    <a href={primaryCta.href}>{primaryCta.label}</a>
                  ) : (
                    primaryCta.label
                  )}
                </Button>
              )}
              {secondaryCta && (
                <Button
                  variant={secondaryCta.variant ?? "outline"}
                  size="lg"
                  {...(secondaryCta.onClick ? { onClick: secondaryCta.onClick } : {})}
                  aria-label={secondaryCta.ariaLabel}
                  data-testid={testId ? `${testId}-cta-secondary` : "testimonials-cta-secondary"}
                  asChild={!!secondaryCta.href}
                >
                  {secondaryCta.href ? (
                    <a href={secondaryCta.href}>{secondaryCta.label}</a>
                  ) : (
                    secondaryCta.label
                  )}
                </Button>
              )}
            </Cluster>
          )}
        </Stack>
      </Container>
    </Section>
  );
}

Testimonials.displayName = "Testimonials";
TestimonialCard.displayName = "TestimonialCard";

// =============================================================================
// EXPORTS
// =============================================================================


export { Testimonials, TestimonialCard, StarRating, Avatar,   };
